import { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Navigation,
  Pagination,
  Autoplay,
} from "swiper/modules";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import styles from "./Products.module.css";
import ProductCard from "./ProductCard";
import {
  fetchBookCategories,
  fetchBooks,
} from "./productsSlice";

const Products = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();

  const [selectedCategoryId, setSelectedCategoryId] = useState(0);

  const books = useSelector(function (state) {
    return state.products.books;
  });

  const categories = useSelector(function (state) {
    return state.products.categories;
  });

  const booksLoading = useSelector(function (state) {
    return state.products.booksLoading;
  });

  const booksError = useSelector(function (state) {
    return state.products.booksError;
  });

  var currentLanguage = i18n.language || "en";
  currentLanguage = currentLanguage.split("-")[0];

  useEffect(
    function () {
      dispatch(fetchBooks(currentLanguage));
      dispatch(fetchBookCategories(currentLanguage));
    },
    [dispatch, currentLanguage]
  );

  var visibleBooks = books;

  if (selectedCategoryId !== 0) {
    visibleBooks = books.filter(function (book) {
      return (
        book.category &&
        Number(book.category.id) === Number(selectedCategoryId)
      );
    });
  }

  return (
    <section className={styles.swiperSection} id="categories">
      <div className="container">
        <div className="text-center mb-5">
          <span className={styles.subTitle}>
            {t("products.collection", "Featured Collection")}
          </span>

          <h2 className={styles.mainTitle}>
            {t("products.discover", "Products Discovery")}
          </h2>
        </div>

        <div className={styles.categoryFilters}>
          <button
            type="button"
            className={
              selectedCategoryId === 0
                ? `${styles.categoryButton} ${styles.activeCategory}`
                : styles.categoryButton
            }
            onClick={function () {
              setSelectedCategoryId(0);
            }}
          >
            {t("products.all", "All")}
          </button>

          {categories.map(function (category) {
            return (
              <button
                type="button"
                key={category.id}
                className={
                  selectedCategoryId === category.id
                    ? `${styles.categoryButton} ${styles.activeCategory}`
                    : styles.categoryButton
                }
                onClick={function () {
                  setSelectedCategoryId(category.id);
                }}
              >
                {category.name}
              </button>
            );
          })}
        </div>

        {booksLoading && (
          <p className="text-center text-white">
            {t("common.loading", "Loading...")}
          </p>
        )}

        {booksError && (
          <p className="text-center text-danger">{booksError}</p>
        )}

        {!booksLoading &&
          !booksError &&
          visibleBooks.length === 0 && (
            <p className="text-center text-white">
              {t("products.noBooks", "No books are available.")}
            </p>
          )}

        {!booksLoading &&
          !booksError &&
          visibleBooks.length > 0 && (
            <>
              <Swiper
                key={
                  selectedCategoryId +
                  "-" +
                  visibleBooks.length
                }
                dir="ltr"
                modules={[Navigation, Pagination, Autoplay]}
                spaceBetween={30}
                slidesPerView={1}
                centeredSlides={false}
                loop={visibleBooks.length > 4}
                autoplay={
                  visibleBooks.length > 1
                    ? {
                        delay: 3000,
                        disableOnInteraction: false,
                      }
                    : false
                }
                pagination={{
                  clickable: true,
                  el: `.${styles.customPagination}`,
                }}
                navigation={{
                  nextEl: `.${styles.navNext}`,
                  prevEl: `.${styles.navPrev}`,
                }}
                breakpoints={{
                  576: {
                    slidesPerView: 1.5,
                    centeredSlides: true,
                  },
                  768: {
                    slidesPerView: 2.5,
                  },
                  1024: {
                    slidesPerView: 3.5,
                  },
                }}
                className={styles.mySwiper}
              >
                {visibleBooks.map(function (book) {
                  return (
                    <SwiperSlide
                      key={book.id}
                      className={styles.swiperSlide}
                    >
                      <ProductCard product={book} />
                    </SwiperSlide>
                  );
                })}
              </Swiper>

              <div className="d-flex justify-content-center align-items-center gap-3 mt-5">
                <button
                  type="button"
                  className={styles.navPrev}
                  aria-label="Previous books"
                >
                  <i className="bi bi-arrow-left"></i>
                </button>

                <div className={styles.customPagination}></div>

                <button
                  type="button"
                  className={styles.navNext}
                  aria-label="Next books"
                >
                  <i className="bi bi-arrow-right"></i>
                </button>
              </div>
            </>
          )}
      </div>
    </section>
  );
};

export default memo(Products);